import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  Flex,
  Heading,
  Table,
  Text,
  TextArea,
  Tooltip,
} from '@radix-ui/themes'
import { useState } from 'react'
import {
  PamDocumentContent,
  PamDocumentSummary,
  PamDuplicateCandidate,
  PamPlayerVerificationStatus,
  PamVerificationDetail,
} from '@Generated/back-office-client/src'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { Modal } from '@Components/common/modal'
import { JsonView } from '@Components/common/json-view'
import { getStatusDisplay } from './pam-verification-status-display'
import { getAccountStatusDisplay } from './pam-player-status-display'
import { PamActionStatus, PamDecisionAction } from './types'
import { PamVerificationDocuments } from './pam-verification-documents'
import { PamVerificationDocumentPreview } from './pam-verification-document-preview'

const ACTION_LABEL: Record<PamDecisionAction, string> = {
  [PamDecisionAction.APPROVE]: 'Approve',
  [PamDecisionAction.DENY]: 'Deny',
  [PamDecisionAction.UNLOCK]: 'Unlock',
}

const formatCandidateAddress = (address: PamDuplicateCandidate['address']): string => {
  if (!address) {
    return '—'
  }
  const line2 = address.line2 ? `, ${address.line2}` : ''
  return `${address.line1}${line2}, ${address.city}, ${address.state} ${address.zip}`
}

export type PamVerificationDetailViewProps = {
  onBack: () => void
  canWrite: boolean
  actionStatus: PamActionStatus
  onDecision: (action: PamDecisionAction, reason: string) => Promise<void>
  documents: PamDocumentSummary[]
  isUploading: boolean
  uploadError: string | null
  onUpload: (file: File) => void
  onDelete: (document: PamDocumentSummary) => void
  previewDocument: PamDocumentSummary | null
  previewContent: PamDocumentContent | null
  previewLoading: boolean
  onPreview: (document: PamDocumentSummary) => void
  onClosePreview: () => void
  onViewProfile: (lookupId: string) => void
  detail?: PamVerificationDetail
}

export const PamVerificationDetailView = ({
  onBack,
  canWrite,
  actionStatus,
  onDecision,
  documents,
  isUploading,
  uploadError,
  onUpload,
  onDelete,
  previewDocument,
  previewContent,
  previewLoading,
  onPreview,
  onClosePreview,
  onViewProfile,
  detail,
}: PamVerificationDetailViewProps) => {
  const [openAction, setOpenAction] = useState<PamDecisionAction | null>(null)
  const [reason, setReason] = useState('')

  if (detail === undefined) {
    return null
  }

  const isApproved =
    detail.status === PamPlayerVerificationStatus.approved ||
    detail.status === PamPlayerVerificationStatus.manually_approved
  const isDenied = detail.status === PamPlayerVerificationStatus.denied
  const pii = detail.submittedPii

  const openModal = (action: PamDecisionAction) => {
    setReason('')
    setOpenAction(action)
  }

  const submit = async () => {
    if (openAction === null || reason.trim().length === 0) {
      return
    }
    try {
      await onDecision(openAction, reason.trim())
      setOpenAction(null)
    } catch {
      // Error surfaced to the user via actionStatus; keep the modal open to retry.
    }
  }

  const historyRows = detail.history.map((entry, index) => {
    return (
      <Table.Row key={`${entry.createdAt}-${index}`}>
        <Table.Cell>{entry.createdAt}</Table.Cell>
        <Table.Cell>
          {entry.fromStatus ?? '—'} → {entry.toStatus}
        </Table.Cell>
        <Table.Cell>{entry.changedBy}</Table.Cell>
        <Table.Cell>{entry.reason ?? entry.reasonCode ?? '—'}</Table.Cell>
        <Table.Cell>
          {entry.vendorRawResponse ? <JsonView data={entry.vendorRawResponse as object} /> : '—'}
        </Table.Cell>
      </Table.Row>
    )
  })

  return (
    <Flex direction="column" gap="5">
      <Flex align="center" justify="between">
        <Flex align="center" gap="3">
          <Heading size="6">{detail.displayName || detail.clerkUserId}</Heading>
          {(() => {
            const display = getStatusDisplay(detail.status)
            return (
              <Tooltip content={display.tooltip}>
                <Badge size="2" color={display.color} variant="soft">
                  {display.label}
                </Badge>
              </Tooltip>
            )
          })()}
          {(() => {
            const accountDisplay = getAccountStatusDisplay(detail.accountStatus)
            return (
              <Badge size="2" color={accountDisplay.color} variant="soft">
                {accountDisplay.label}
              </Badge>
            )
          })()}
        </Flex>
        <Button onClick={onBack}>Back</Button>
      </Flex>

      <Card>
        <DataList.Root>
          <DataList.Item>
            <DataList.Label>Email</DataList.Label>
            <DataList.Value>{detail.email ?? '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Name (submitted)</DataList.Label>
            <DataList.Value>{`${pii.firstName} ${pii.lastName}`.trim() || '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Date of birth</DataList.Label>
            <DataList.Value>{pii.dob}</DataList.Value>
          </DataList.Item>
          {pii.ssn4 && (
            <DataList.Item>
              <DataList.Label>SSN (last 4)</DataList.Label>
              <DataList.Value>{pii.ssn4}</DataList.Value>
            </DataList.Item>
          )}
          <DataList.Item>
            <DataList.Label>Phone</DataList.Label>
            <DataList.Value>{pii.phone}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Address</DataList.Label>
            <DataList.Value>
              {pii.address.line1}
              {pii.address.line2 ? `, ${pii.address.line2}` : ''}, {pii.address.city},{' '}
              {pii.address.state} {pii.address.zip}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Attempts</DataList.Label>
            <DataList.Value>
              {detail.attemptCount} / {detail.maxAttempts}
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </Card>

      {(detail.duplicateCandidates.length > 0 ||
        detail.status === PamPlayerVerificationStatus.duplicate_review) && (
        <Box>
          <Heading size="4" mb="2">
            Potential duplicate accounts
          </Heading>
          {detail.duplicateCandidates.length === 0 ? (
            <Callout.Root color="gray" variant="soft">
              <Text size="2">
                Flagged as a potential duplicate, but no account currently shares these identity
                details — the matching account(s) may have been resolved or had their details
                edited.
              </Text>
            </Callout.Root>
          ) : (
            (() => {
              const showSsn = detail.duplicateCandidates.some((c) => c.ssn4 != null)
              return (
                <Table.Root variant="surface">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Date of birth</Table.ColumnHeaderCell>
                      {showSsn && <Table.ColumnHeaderCell>SSN (last 4)</Table.ColumnHeaderCell>}
                      <Table.ColumnHeaderCell>Address</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Account</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Verification</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {detail.duplicateCandidates.map((candidate) => {
                      const accountDisplay = getAccountStatusDisplay(candidate.accountStatus)
                      const verificationDisplay = getStatusDisplay(candidate.verificationStatus)
                      return (
                        <Table.Row key={candidate.lookupId}>
                          <Table.Cell>{candidate.displayName || '—'}</Table.Cell>
                          <Table.Cell>{candidate.email ?? '—'}</Table.Cell>
                          <Table.Cell>{candidate.dob ?? '—'}</Table.Cell>
                          {showSsn && <Table.Cell>{candidate.ssn4 ?? '—'}</Table.Cell>}
                          <Table.Cell>{formatCandidateAddress(candidate.address)}</Table.Cell>
                          <Table.Cell>
                            <Badge color={accountDisplay.color} variant="soft">
                              {accountDisplay.label}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge color={verificationDisplay.color} variant="soft">
                              {verificationDisplay.label}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              size="1"
                              variant="soft"
                              color="gray"
                              onClick={() => onViewProfile(candidate.lookupId)}>
                              View profile
                              <ChevronRightIcon />
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Root>
              )
            })()
          )}
        </Box>
      )}

      {canWrite && (
        <Flex gap="3">
          {!isApproved && (
            <Button onClick={() => openModal(PamDecisionAction.APPROVE)}>Approve</Button>
          )}
          {!isApproved && !isDenied && (
            <Button color="red" variant="soft" onClick={() => openModal(PamDecisionAction.DENY)}>
              Deny
            </Button>
          )}
        </Flex>
      )}

      <PamVerificationDocuments
        documents={documents}
        canWrite={canWrite}
        isUploading={isUploading}
        uploadError={uploadError}
        onUpload={onUpload}
        onPreview={onPreview}
        onDelete={onDelete}
      />

      <Box>
        <Heading size="4" mb="2">
          History
        </Heading>
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Transition</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>By</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Vendor response</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{historyRows}</Table.Body>
        </Table.Root>
      </Box>

      {openAction !== null && (
        <Modal
          title={`${ACTION_LABEL[openAction]} verification`}
          description="This action is recorded in the audit trail. A reason is required."
          onOpenChange={(open) => {
            if (!open) {
              setOpenAction(null)
            }
          }}>
          <Flex direction="column" gap="3">
            <TextArea
              placeholder="Reason (required)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {actionStatus === PamActionStatus.ERROR && (
              <Text color="red" size="2">
                Something went wrong. Please try again.
              </Text>
            )}
            <Flex gap="3" justify="end">
              <Button variant="soft" color="gray" onClick={() => setOpenAction(null)}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={reason.trim().length === 0 || actionStatus === PamActionStatus.PENDING}>
                {ACTION_LABEL[openAction]}
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}

      <PamVerificationDocumentPreview
        open={previewDocument !== null}
        fileName={previewDocument?.fileName ?? null}
        content={previewContent}
        isLoading={previewLoading}
        onClose={onClosePreview}
      />
    </Flex>
  )
}
